import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import * as signalsApi from '../services/signals-api.js';
import type { SignalDecision, SimilarityThresholds } from '../services/signals-api.js';
import * as copilotApi from '../services/copilot-api.js';
import { navigateTo } from '../services/router.js';
import '../components/copilot/cp-copilot-panel.js';

/**
 * Signal-Eingang (compass-new Projektionslayer) — read-only Übersicht aller eingegangenen
 * Signale + Decision-Outcome. Liest das Read-Model proj_signal_decisions.
 */
@customElement('cp-page-signals')
export class CpPageSignals extends LitElement {
  createRenderRoot() { return this; }

  @state() private _signals: SignalDecision[] = [];
  @state() private _routes: Record<string, number> = {};
  @state() private _total = 0;
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _thresholds: SimilarityThresholds = { relevant: 0.78, unclear: 0.68 };

  override connectedCallback(): void {
    super.connectedCallback();
    void this._load();
  }

  private async _load(): Promise<void> {
    this._loading = true;
    this._error = '';
    try {
      const [res, thresholds] = await Promise.all([
        signalsApi.listSignalDecisions(),
        signalsApi.getThresholds().catch(() => this._thresholds),
      ]);
      this._signals = res.signals;
      this._routes = res.routes;
      this._total = res.total;
      this._thresholds = thresholds;
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden';
    } finally {
      this._loading = false;
    }
  }

  @state() private _rebuilding = false;

  private _categoryColor(cat: string | null): string {
    switch (cat) {
      case 'Projektanfrage': return '#1565c0';
      case 'Bewerbung': return '#6a1b9a';
      case 'Provisions-Angebot': return '#b71c1c';
      case 'Laufender-Vorgang': return '#00838f';
      case 'Operativ': return '#546e7a';
      case 'Irrelevant': return '#757575';
      case 'Unklar': return '#795548';
      default: return '#9e9e9e';
    }
  }

  private _routeColor(route: string | null): string {
    switch (route) {
      case 'new-substrategy': return '#2e7d32';
      case 'process-note': return '#5c6bc0';
      case 'deferred': return '#b8860b';
      case 'ignore+log': return '#888';
      default: return '#bbb';
    }
  }

  /** Klartext-Bedeutung einer Decision-Route. */
  private _routeMeaning(route: string | null): { label: string; hint: string } {
    switch (route) {
      case 'new-substrategy': return { label: 'Relevant', hint: 'passt zu einem Ziel → Teilstrategie' };
      case 'process-note': return { label: 'Notiz', hint: 'laufender Vorgang / operativer Kontakt' };
      case 'deferred': return { label: 'Unklar', hint: 'Constraint fehlt — Goal-Tree ergänzen' };
      case 'ignore+log': return { label: 'Ignoriert', hint: 'nicht relevant für deine Ziele' };
      default: return { label: 'Ausstehend', hint: 'noch nicht bewertet' };
    }
  }

  private async _rebuild(): Promise<void> {
    this._rebuilding = true; this._error = '';
    try {
      const res = await signalsApi.rebuildSignalDecisions();
      this._signals = res.signals; this._routes = res.routes; this._total = res.total;
    } catch (e) { this._error = e instanceof Error ? e.message : 'Fehler'; }
    finally { this._rebuilding = false; }
  }

  private _shortStream(s: string): string {
    return s.replace(/(-emp[^-]*-|-applai-[^-]+-)/, '-…-');
  }

  /** SQLite-UTC-Datum ("YYYY-MM-DD HH:MM:SS") → deutsches Format in Europe/Berlin. */
  private _fmtDate(s: string | null): string {
    if (!s) return '—';
    const hasTz = /[zZ]|[+-]\d\d:?\d\d$/.test(s);
    const d = new Date(s.includes('T') ? s : s.replace(' ', 'T') + (hasTz ? '' : 'Z'));
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString('de-DE', {
      timeZone: 'Europe/Berlin',
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }

  override render() {
    return html`
      <style>
        .sig-page { padding: 1.5rem; max-width: 1500px; margin: 0 auto; display: grid; grid-template-columns: 1fr 380px; gap: 1.5rem; align-items: start; }
        .sig-copilot { position: sticky; top: 1rem; height: calc(100vh - 6rem); }
        @media (max-width: 1100px) { .sig-page { grid-template-columns: 1fr; } .sig-copilot { position: static; height: 520px; } }
        .sig-wrap { font-family: var(--font-family-sans); color: var(--color-text-primary); min-width: 0; }
        .sig-head { display: flex; align-items: baseline; gap: 1rem; margin-bottom: 1rem; }
        .sig-head h1 { font-size: 1.4rem; margin: 0; color: var(--color-text-primary); }
        .sig-sub { color: var(--color-text-secondary); font-size: .9rem; }
        .sig-chips { display: flex; gap: .5rem; flex-wrap: wrap; margin-bottom: 1rem; }
        .sig-chip { padding: .2rem .6rem; border-radius: 999px; color: #fff; font-size: .8rem; }
        table.sig { width: 100%; border-collapse: collapse; font-size: .88rem; }
        table.sig th, table.sig td { text-align: left; padding: .5rem .6rem; border-bottom: 1px solid var(--color-border); vertical-align: top; }
        table.sig th { color: var(--color-text-secondary); font-weight: 600; }
        table.sig td { color: var(--color-text-primary); }
        .sig-route { font-weight: 600; }
        .sig-intro { color: var(--color-text-secondary); font-size: .86rem; line-height: 1.5; margin-bottom: 1rem; }
        .sig-badge { display: inline-block; font-weight: 600; font-size: .78rem; padding: .12rem .55rem; border-radius: 999px; color: #fff; }
        .sig-hint { color: var(--color-text-muted); font-size: .76rem; margin-top: .15rem; }
        .sig-title { color: var(--color-text-primary); }
        .sig-title.empty { color: var(--color-text-muted); }
        .sig-missing { color: var(--color-accent-warning); font-size: .8rem; }
        .sig-empty { color: var(--color-text-muted); padding: 2rem 0; }
        .sig-refresh { margin-left: auto; cursor: pointer; border: 1px solid var(--color-border); background: var(--color-bg-tertiary); color: var(--color-text-primary); border-radius: 6px; padding: .3rem .7rem; }
        .sig-refresh:hover { border-color: var(--color-border-hover); background: var(--color-bg-hover); }
        .sig-score { font-variant-numeric: tabular-nums; font-size: .88rem; }
        .sig-score-na { color: var(--color-text-muted); }
        .sig-pillar { color: var(--color-text-secondary); font-size: .76rem; margin-top: .15rem; }
        .sig-cat-badge { display: inline-block; font-weight: 600; font-size: .75rem; padding: .1rem .5rem; border-radius: 999px; color: #fff; white-space: nowrap; }
        .sig-cat-reason { color: var(--color-text-muted); font-size: .74rem; margin-top: .15rem; line-height: 1.4; }
        .sig-cat-conf { color: var(--color-text-muted); font-size: .72rem; margin-top: .1rem; }
      </style>
      <div class="sig-page">
      <div class="sig-wrap">
        <div class="sig-head">
          <h1>Signal-Eingang</h1>
          <span class="sig-sub">${this._total} Signale · Projektionslayer (read-only)</span>
          <button class="sig-refresh" @click=${() => navigateTo('/signal-sources')}>⚙ Quellen konfigurieren</button>
          <button class="sig-refresh" ?disabled=${this._rebuilding} @click=${() => void this._rebuild()}>${this._rebuilding ? '… baut neu' : '⟳ Neu aufbauen'}</button>
          <button class="sig-refresh" @click=${() => void this._load()}>↻ Neu laden</button>
        </div>

        <p class="sig-intro">
          Jede Zeile = ein eingegangenes Signal (z.B. eine Mail) und wie die Decision es bewertet hat:
          <strong>Relevant</strong> (passt zu einem Ziel) · <strong>Notiz</strong> (laufender Vorgang / operativ) ·
          <strong>Unklar</strong> (Constraint fehlt) · <strong>Ignoriert</strong> (nicht relevant).
          Die Spalte <em>Ähnlichkeit</em> zeigt den Cosine-Score zum besten Ziel
          (≥ ${this._thresholds.relevant.toFixed(2)} → relevant, ≥ ${this._thresholds.unclear.toFixed(2)} → unklar).
          Die Spalte <em>Kategorie</em> zeigt die LLM-Klassifikation (Claude Haiku) der Absicht:
          <strong>Projektanfrage</strong> · <strong>Bewerbung</strong> · <strong>Provisions-Angebot</strong> ·
          <strong>Laufender-Vorgang</strong> · <strong>Operativ</strong> · <strong>Irrelevant</strong> · <strong>Unklar</strong>.
          „Neu aufbauen" löst Re-Evaluation mit aktuellen Embeddings aus und faltet das Read-Model neu.
        </p>

        ${this._error ? html`<p style="color:#c33">${this._error}</p>` : nothing}
        ${this._loading ? html`<p>Lädt…</p>` : nothing}

        <div class="sig-chips">
          ${Object.entries(this._routes).map(([route, n]) => html`
            <span class="sig-chip" style="background:${this._routeColor(route)}" title=${this._routeMeaning(route).hint}>${this._routeMeaning(route).label}: ${n}</span>
          `)}
        </div>

        ${!this._loading && this._signals.length === 0
          ? html`<div class="sig-empty">Noch keine Signale. Quelle anlegen + „Jetzt ausführen" (⚙ Quellen konfigurieren).</div>`
          : html`
          <table class="sig">
            <thead>
              <tr><th>Quelle</th><th>Worum geht's</th><th title="Cosine-Ähnlichkeit (Embeddings) · ≥${this._thresholds.relevant.toFixed(2)} relevant · ≥${this._thresholds.unclear.toFixed(2)} unklar">Ähnlichkeit</th><th title="LLM-Klassifikation der Absicht (Claude Haiku)">Kategorie</th><th>Entscheidung</th><th>Eingang</th></tr>
            </thead>
            <tbody>
              ${this._signals.map((s) => {
                const m = this._routeMeaning(s.decisionRoute);
                return html`
                <tr>
                  <td>${s.source ?? '—'}</td>
                  <td title=${this._shortStream(s.signalStreamId)}>
                    <span class="sig-title ${s.projectTitle ? '' : 'empty'}">${s.projectTitle ?? 'ohne Titel'}</span>
                  </td>
                  <td>
                    ${s.decisionScore != null
                      ? html`<span class="sig-score" title="Cosine-Ähnlichkeit · ≥${this._thresholds.relevant.toFixed(2)} relevant · ≥${this._thresholds.unclear.toFixed(2)} unklar">${s.decisionScore.toFixed(2)}</span>
                               ${s.matchedPillar ? html`<div class="sig-pillar">${s.matchedPillar}</div>` : nothing}`
                      : html`<span class="sig-score-na">${s.matchScore ?? '—'}</span>`}
                  </td>
                  <td>
                    ${s.category
                      ? html`
                        <span class="sig-cat-badge" style="background:${this._categoryColor(s.category)}">${s.category}</span>
                        ${s.categoryReason ? html`<div class="sig-cat-reason">${s.categoryReason}</div>` : nothing}
                        ${s.categoryConfidence != null ? html`<div class="sig-cat-conf">${Math.round(s.categoryConfidence * 100)} %</div>` : nothing}
                      `
                      : html`<span style="color:var(--color-text-muted);font-size:.8rem">—</span>`}
                  </td>
                  <td>
                    <span class="sig-badge" style="background:${this._routeColor(s.decisionRoute)}">${m.label}</span>
                    <div class="sig-hint">${m.hint}</div>
                    ${s.missingConstraints && s.missingConstraints.length
                      ? html`<div class="sig-missing">fehlt: ${s.missingConstraints.join(', ')}</div>` : nothing}
                  </td>
                  <td title=${s.receivedAt ?? ''}>${this._fmtDate(s.receivedAt)}</td>
                </tr>
              `;})}
            </tbody>
          </table>`}
      </div>
      <aside class="sig-copilot">
        <cp-copilot-panel
          heading="Signal-Copilot"
          subtitle="Frag, warum ein Signal wie geroutet wurde."
          placeholder="z.B. Warum ist Signal X deferred?"
          example="Welche Signale hängen an fehlenden Constraints — und welcher Goal-Tree-Constraint würde helfen?"
          .send=${(m: string) => copilotApi.askSignals(m)}
        ></cp-copilot-panel>
      </aside>
      </div>
    `;
  }
}
